// BARNES Design System tokens (ADR 0001). Source of truth: tokens/tokens.json of the design
// system project; update values there first, then here.

// Semantic layer: the only colors new or migrated code uses.
const semanticColors = {
  text: "#000000",
  textMuted: "#4A4A4A",
  textSubtle: "#767676",
  textInverse: "#FFFFFF",
  surface: "#FFFFFF",
  surfaceAlt: "#F7F5F1",
  surfaceGreige: "#EAE6DD",
  surfaceInverse: "#000000",
  surfaceInverseAlt: "#1A1A1A",
  surfaceHover: "#F7F5F1",
  line: "#D8D4CB",
  lineStrong: "#B5AFA3",
  lineInverse: "#3A3A3A",
  accent: "#C70A33",
  accentHover: "#A50829",
  focus: "#C70A33",
  onAccent: "#FFFFFF",
  success: "#1F6B45",
  warning: "#8A6A16",
  danger: "#C70A33",
  // Drawer overlay (drawer.css): the list stays visible behind the panel (ADR 0003).
  drawerOverlay: "rgba(0, 0, 0, 0.45)",
}

// Inverse theme (application sidebar): semantic overrides on a black surface.
export const inverseColors = {
  text: "#FFFFFF",
  textMuted: "#B5AFA3",
  textSubtle: "#B5AFA3",
  textInverse: "#000000",
  surface: "#000000",
  surfaceAlt: "#3A3A3A",
  surfaceHover: "#1A1A1A",
  line: "#3A3A3A",
  lineStrong: "#B5AFA3",
}

// Chakra's built-in palettes, re-pointed to design system values: application code never uses
// them (semantic tokens only), but Chakra does internally (invalid fields, toasts, avatars,
// default text and border colors). Light shades stay neutral: no tinted backgrounds.
const scale = (light: string, base: string, dark: string) => ({
  50: semanticColors.surfaceAlt,
  100: semanticColors.surfaceAlt,
  200: light,
  300: light,
  400: base,
  500: base,
  600: dark,
  700: dark,
  800: dark,
  900: dark,
})
const neutral = scale(semanticColors.lineStrong, semanticColors.text, semanticColors.text)
const chakraPalettes = {
  gray: {
    50: semanticColors.surfaceAlt,
    100: semanticColors.surfaceGreige,
    200: semanticColors.line,
    300: semanticColors.lineStrong,
    400: semanticColors.textSubtle,
    500: semanticColors.textSubtle,
    600: semanticColors.textMuted,
    700: semanticColors.lineInverse,
    800: semanticColors.surfaceInverseAlt,
    900: semanticColors.text,
  },
  red: scale(semanticColors.lineStrong, semanticColors.danger, semanticColors.accentHover),
  green: scale(semanticColors.lineStrong, semanticColors.success, semanticColors.success),
  orange: scale(semanticColors.lineStrong, semanticColors.warning, semanticColors.warning),
  yellow: scale(semanticColors.lineStrong, semanticColors.warning, semanticColors.warning),
  teal: neutral,
  blue: neutral,
  cyan: neutral,
  purple: neutral,
  pink: neutral,
  linkedin: neutral,
  facebook: neutral,
  messenger: neutral,
  whatsapp: neutral,
  twitter: neutral,
  telegram: neutral,
}

export const colors = {
  ...chakraPalettes,
  ...semanticColors,
  brand: {
    black: "#000000",
    white: "#FFFFFF",
    red: "#C70A33",
    greige: "#EAE6DD",
  },
}

export const ralewayFontString =
  "'Twemoji Country Flags', 'Raleway', 'Helvetica Neue', Arial, sans-serif"

export const fonts = {
  body: ralewayFontString,
  heading: ralewayFontString,
  mono: ralewayFontString,
}

export const fontWeights = {
  light: 300,
  normal: 400,
  medium: 600,
  semibold: 600,
  bold: 700,
}

export const fontSizes = {
  "3xs": "11px",
  "2xs": "12px",
  xs: "13px",
  sm: "14px",
  md: "16px",
  lg: "18px",
  xl: "22px",
  "2xl": "28px",
  "3xl": "36px",
  "4xl": "44px",
  "5xl": "56px",
}

export const lineHeights = {
  tight: 1.15,
  snug: 1.3,
  normal: 1.6,
  loose: 1.8,
}

export const letterSpacings = {
  normal: "0",
  wide: "0.06em",
  wider: "0.12em",
  widest: "0.18em",
}

export const radii = {
  none: "0",
  sm: "4px",
  md: "6px",
  lg: "12px",
  full: "999px",
}

const shadowCard = "0 2px 12px rgba(0, 0, 0, 0.08)"

export const shadows = {
  card: shadowCard,
  overlay: "0 8px 40px rgba(0, 0, 0, 0.16)",
  // Focus is drawn by the global :focus-visible outline (theme.ts), as in the design system;
  // Chakra's own focus box-shadow is disabled so the ring is never doubled.
  outline: "none",
  M: shadowCard,
}

export const sizes = {
  controlH: "48px",
  controlHSm: "40px",
  rowHCompact: "40px",
  sidebarW: "256px",
  sidebarWCollapsed: "72px",
  // Seal zone of the sidebar and title band of a section page: the menu entries start under the
  // page title (SubNavLayout), from lg.
  pageHeaderH: "104px",
}

export const textStyles = {
  h1: {
    fontSize: "5xl",
    lineHeight: "tight",
    fontWeight: "light",
  },
  h2: {
    fontSize: "4xl",
    lineHeight: "tight",
    fontWeight: "light",
  },
  h3: {
    fontSize: "3xl",
    lineHeight: "tight",
    fontWeight: "light",
  },
  h4: {
    fontSize: "2xl",
    lineHeight: "snug",
    fontWeight: "light",
  },
  h5: {
    fontSize: "xl",
    lineHeight: "snug",
    fontWeight: "light",
  },
  p1: {
    fontSize: "lg",
    lineHeight: "normal",
    fontWeight: "normal",
  },
  p2: {
    fontSize: "md",
    lineHeight: "normal",
    fontWeight: "normal",
  },
  p3: {
    fontSize: "sm",
    lineHeight: "normal",
    fontWeight: "normal",
  },
  p4: {
    fontSize: "2xs",
    lineHeight: "normal",
    fontWeight: "normal",
  },
  linkL: {
    fontSize: "xl",
    lineHeight: "snug",
    fontWeight: "semibold",
  },
  linkM: {
    fontSize: "md",
    lineHeight: "snug",
    fontWeight: "semibold",
  },
  linkS: {
    fontSize: "sm",
    lineHeight: "snug",
    fontWeight: "semibold",
  },
  label: {
    fontSize: "3xs",
    fontWeight: "semibold",
    letterSpacing: "wider",
    textTransform: "uppercase",
  },
  eyebrow: {
    fontSize: "2xs",
    fontWeight: "semibold",
    letterSpacing: "widest",
    textTransform: "uppercase",
  },
}
